/**
 * Analysis Constants
 *
 * Shared defaults for the analysis pipeline that must stay identical between
 * the backend (which computes them) and the frontend (which displays them as
 * a fallback when no project-level override is set).
 */

/**
 * Default minimum call duration (seconds) for the Level 2 "short call" filter.
 *
 * Calls with less conversation than this are transcribed but NOT analyzed (not
 * enough content for meaningful QA). Used as the fallback when a project has no
 * `minCallDurationSeconds` override in its settings — both when computing
 * `isShortCall` at transcribe time and when displaying the threshold in the UI.
 * Single source of truth: change it here, not at each call site.
 *
 * Measured against `computeSpeechSeconds` (conversation), not the length of the
 * recording — see that function for why.
 */
export const DEFAULT_MIN_CALL_DURATION_SECONDS = 30;

/**
 * Default minimum chat length (messages) for the chat significance filter.
 *
 * Чаты короче этого транскрибируются (текст приходит готовым из CRM), но НЕ
 * анализируются: содержания слишком мало для оценки. Считаются только реплики
 * `agent` и `customer`, `system` не в счёт. Fallback, когда у проекта нет
 * `minChatMessages` в настройках — и в бэкенде, и в UI.
 *
 * Замер прода 2026-08-21: вместе с правилом «оператор должен ответить» порог 4
 * отсекает 162 из 435 чатов (37 %), при среднем живом чате в 3.4 реплики
 * оператора и 5.5 клиента.
 */
export const DEFAULT_MIN_CHAT_MESSAGES = 4;

/**
 * Window (days) behind the average quality score shown on a project card.
 *
 * Two reasons it is a window rather than all history:
 *
 * 1. Cost. The average joins every analysis a project ever had. On the largest
 *    production project that was 26,374 analyses — 141,275 rows read in 1.3 s
 *    (measured 2026-08-11) — and it only ever grows, so the projects page got
 *    slower every week on its own.
 * 2. Meaning. An all-time average stops moving once enough calls accumulate:
 *    a team that fixed its scripts last month sees the same number as before,
 *    which is precisely when the number should have reacted.
 *
 * Shared so the figure and its label cannot disagree: the backend filters by
 * it, the card names it in the caption.
 */
export const PROJECT_CARD_SCORE_WINDOW_DAYS = 30;

/**
 * Window of the per-organization record count in the owner's health matrix.
 *
 * Shared for the same reason as {@link PROJECT_CARD_SCORE_WINDOW_DAYS}: the
 * backend counts over it and the matrix prints it next to the number, so a
 * change here cannot leave the label saying an old window (#1098).
 */
export const ORG_MATRIX_RECORD_WINDOW_DAYS = 30;

/**
 * Minimum per-objection rubric score that counts as handled.
 *
 * The primary model scores each objection, but it does not own the boolean or
 * the aggregate. The analysis finalizer derives both from this shared
 * threshold, and read-side consumers use the same value as a defensive
 * fallback for historical/pre-finalizer payloads.
 */
export const OBJECTION_HANDLED_SCORE_THRESHOLD = 60;

/** Lower bound for {@link PROJECT_CARD_SCORE_WINDOW_DAYS}, as a full ISO string. */
export function projectCardScoreWindowStart(now: Date = new Date()): string {
  const since = new Date(now);
  since.setUTCDate(since.getUTCDate() - PROJECT_CARD_SCORE_WINDOW_DAYS);
  return since.toISOString();
}

/** A recognised word with its position in the recording. */
export interface TimedWord {
  start: number;
  end: number;
}

/**
 * How long the conversation lasted: first recognised word to last.
 *
 * NOT the length of the recording. The two diverge whenever a recording carries
 * dead air the caller never spoke through — ringing, an IVR greeting, hold
 * music, silence after goodbye. The case that forced this apart: an outbound
 * call answered by the far end's answering machine, reported by the telephony
 * provider as 80 seconds of answered talk time, whose entire transcript was a
 * recorded greeting and a music tag. Eighty seconds of recording, no
 * conversation at all — analysed and billed as if it were a call.
 *
 * Deliberately the SPAN and not the sum of speech, nor the ratio of speech to
 * recording. A ratio cannot separate the two cases that matter: ten minutes on
 * hold followed by a real minute of talk is ~9% speech, and an answering
 * machine is ~2% — any threshold catching the second discards the first, which
 * is a genuine conversation. The span reads them correctly (60 s vs 9 s). It
 * also leaves ordinary calls alone: on a call without dead air the span equals
 * the recording, whereas summing speech would subtract every natural pause.
 *
 * Returns `undefined`, never 0, when there is nothing to measure. The caller
 * must fall back to the recording length: an ASR provider that returns no word
 * timestamps would otherwise mark every call unanalysable and unbillable at
 * once. Absence of the signal must not be read as absence of conversation.
 */
export function computeSpeechSeconds(words?: TimedWord[]): number | undefined {
  if (!words || words.length === 0) return undefined;

  let first = Infinity;
  let last = -Infinity;

  for (const word of words) {
    // Providers have been seen to emit a word with only one endpoint; take
    // whichever bounds are real rather than letting a NaN poison the span.
    if (Number.isFinite(word.start) && word.start < first) first = word.start;
    if (Number.isFinite(word.end) && word.end > last) last = word.end;
  }

  if (!Number.isFinite(first) || !Number.isFinite(last)) return undefined;

  // A single word spans a fraction of a second; never let rounding turn a real
  // (if tiny) conversation into "no conversation", which is a different claim.
  const span = last - first;
  return span > 0 ? span : undefined;
}

/**
 * The conversation can never be longer than the recording it was measured in.
 *
 * That ought to be true by construction — the span lives inside the file — but
 * the two numbers do not always come from the same place. `duration_seconds`
 * is only overwritten from ASR when the provider reports a positive duration;
 * otherwise the row keeps the length the telephony CDR supplied, and a CDR
 * reports *talk* time (`answer_seconds`), which can be shorter than the audio
 * it hands us. A conversation measured at 70 s inside a file the CDR called 60 s
 * is not a longer conversation, it is two numbers that disagree.
 *
 * Clamping rather than discarding, because the two are equivalent where it
 * matters — both leave the recording length driving the gate and the charge —
 * and clamping keeps the invariant callers rely on: what we bill never exceeds
 * the recording. Callers should log when this fires; a silent correction turns
 * a data inconsistency into something nobody ever sees.
 */
export function clampSpeechToRecording(
  speechSeconds: number | undefined,
  recordingSeconds: number | undefined
): number | undefined {
  if (speechSeconds === undefined) return undefined;
  if (recordingSeconds === undefined || !Number.isFinite(recordingSeconds)) {
    return speechSeconds;
  }

  const clamped = Math.min(speechSeconds, recordingSeconds);

  // Zero is not a shorter conversation, it is the same "nothing measurable"
  // `computeSpeechSeconds` refuses to report — and it must stay refusable. A
  // recording length of 0 (a provider that reports none) would otherwise clamp
  // a real span to 0, and `speechSeconds ?? durationSeconds` does NOT fall
  // through 0: the record would be billed nothing, and the usage-charge row is
  // UNIQUE per record, so it could never be billed again.
  return clamped > 0 ? clamped : undefined;
}

/**
 * Severity from which an alert is pushed out through personal channels
 * (email / Telegram / webhook) as soon as it fires. Below it the alert still
 * appears in-app and in digests — it just does not interrupt anyone.
 *
 * Lives in core rather than `@aica/alerts` because the notifications hub in
 * the SPA states this threshold out loud, and `@aica/alerts` is not built for
 * the browser bundle. This is the ONLY definition: `@aica/alerts` imports it
 * (`packages/alerts/src/routing.ts`) and the SPA imports it, so the routing
 * decision and the sentence describing it cannot drift apart. Import it from
 * `@aica/core` — the alerts barrel deliberately does not re-export it, and
 * adding a re-export or a local copy would put the second definition back.
 *
 * The per-org override (`orgs.settings_json.alertInstantMinSeverity`) is read
 * by the alert step but has no writer — no endpoint and no screen sets it — so
 * in practice every organization runs on this value.
 */
export const DEFAULT_INSTANT_MIN_SEVERITY = 'high';
